import { useState, useEffect } from 'react'
import { useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { API_CUSTOMERS_SEARCH } from '@src/config/env'
import { captureWarning } from '../components/Sentry/utils'

const useCustomerSearch = () => {
  const [customerId, setCustomerId] = useState(null)
  const [yotpoDataToken, setYotpoDataToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [customerData, setCustomerData] = useState(null)
  const { email } = useCurrentUser()

  const searchCustomers = async ({ filterKey = 'EMAIL', filterValue, pageSize = 10, nextPageKey = null } = {}) => {
    setLoading(true)
    setError(null)

    try {
      const userSession = await AuthUtil.getUserSession()
      const bearerToken = userSession?.idToken?.jwtToken

      const searchValue = filterValue || email

      const params = new URLSearchParams({
        filterKey,
        filterValue: searchValue,
        pageSize: pageSize.toString(),
      })

      if (nextPageKey) {
        params.append('nextPageKey', nextPageKey)
      }

      const url = `${API_CUSTOMERS_SEARCH}/search/v2`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          // @i18n-ignore
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`)
      }

      const data = await response.json()
      setCustomerData(data)
      setCustomerId(data?.yotpoExternalId || data?.customerId)
      setYotpoDataToken(data?.yotpoDataToken ?? null)

      if (!data?.yotpoDataToken) {
        console.warn('[useCustomerSearch] yotpoDataToken missing from customer search response', {
          hasCustomerId: Boolean(data?.yotpoExternalId || data?.customerId),
        })
        captureWarning(
          'yotpoDataToken missing from customer search response',
          { filterKey, hasCustomerId: Boolean(data?.yotpoExternalId || data?.customerId) },
          { source: 'useCustomerSearch' },
        )
      }

      setLoading(false)
      return data
    } catch (error) {
      console.error('Error searching customers:', error)
      setError(error)
      setLoading(false)
      throw error
    }
  }

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        await searchCustomers({})
      } catch (error) {
        console.error('Error searching customers:', error)
      }
    }
    fetchCustomerDetails()
  }, [])

  return {
    customerId,
    yotpoDataToken,
    customerData,
    loading,
    error,
    searchCustomers,
  }
}

export default useCustomerSearch
